import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOpportunities } from '../contexts/OpportunityContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { adminAPI, adminBillingAPI } from '../services/api';
import type {
  AdminStats,
  AdminUserMetricPoint,
  AdminUserRow,
  AdminContractMetricPoint,
  AdminIndustryOverrideRow,
  AdminBillingOptions,
  AdminIndustryReviewStatus,
} from '../types';
import IndustryOverrideFilters from '../components/admin/IndustryOverrideFilters';
import IndustryOverrideTable from '../components/admin/IndustryOverrideTable';

const Admin: React.FC = () => {
  const { user } = useAuth();
  const { loadOpportunities } = useOpportunities();

  const [needsGate, setNeedsGate] = useState(false);
  const [gateValue, setGateValue] = useState('');
  const [gateSubmitting, setGateSubmitting] = useState(false);
  const [gateError, setGateError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [userMetrics, setUserMetrics] = useState<AdminUserMetricPoint[]>([]);
  const [userMetricsLoading, setUserMetricsLoading] = useState(true);

  const [contractMetrics, setContractMetrics] = useState<AdminContractMetricPoint[]>([]);
  const [contractMetricsLoading, setContractMetricsLoading] = useState(true);

  const [userRows, setUserRows] = useState<AdminUserRow[]>([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [billingModalOpen, setBillingModalOpen] = useState(false);
  const [billingModalUserId, setBillingModalUserId] = useState<number | null>(null);
  const [billingOptions, setBillingOptions] = useState<AdminBillingOptions | null>(null);
  const [billingActionMessage, setBillingActionMessage] = useState<string | null>(null);
  const [billingActionError, setBillingActionError] = useState<string | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingActionLoading, setBillingActionLoading] = useState<string | null>(null);

  const [taxonomy, setTaxonomy] = useState<string[]>([]);
  const [overrideOrgOptions, setOverrideOrgOptions] = useState<string[]>([]);
  const [overrideStatusOptions, setOverrideStatusOptions] = useState<string[]>([]);
  const [overrideRows, setOverrideRows] = useState<AdminIndustryOverrideRow[]>([]);
  const [overrideTotal, setOverrideTotal] = useState(0);
  const [overridePage, setOverridePage] = useState(1);
  const overridePageSize = 25;
  const [overrideOrgFilter, setOverrideOrgFilter] = useState<string[]>([]);
  const [overrideIndustryFilter, setOverrideIndustryFilter] = useState<string[]>([]);
  const [overrideStatusFilter, setOverrideStatusFilter] = useState<string[]>([]);
  const [overrideHasOverride, setOverrideHasOverride] = useState<'all' | 'with' | 'without'>('all');
  const [overrideIncludeClosed, setOverrideIncludeClosed] = useState(false);
  const [overrideSearch, setOverrideSearch] = useState('');
  const [overrideMinDue, setOverrideMinDue] = useState('');
  const [overrideMaxDue, setOverrideMaxDue] = useState('');
  const [overrideSortField, setOverrideSortField] = useState('due_date');
  const [overrideSortDirection, setOverrideSortDirection] = useState<'asc' | 'desc'>('asc');
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideShowNewOnly, setOverrideShowNewOnly] = useState(false);
  const [reviewStatus, setReviewStatus] = useState<AdminIndustryReviewStatus | null>(null);
  const [reviewStatusLoading, setReviewStatusLoading] = useState(false);
  const [markingReview, setMarkingReview] = useState(false);

  const handleAdminError = useCallback((err: any) => {
    const status = err?.response?.status;
    const code = err?.response?.data?.error;
    if (status === 403 && code === 'admin_gate_required') {
      setNeedsGate(true);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);

    return () => window.clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;

    let cancelled = false;
    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const response = await adminAPI.getStats();
        if (!cancelled) {
          setStats(response.data);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load admin stats', err);
        if (!cancelled) setError((prev) => prev ?? 'Unable to load admin statistics');
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };

    const loadUserMetrics = async () => {
      setUserMetricsLoading(true);
      try {
        const response = await adminAPI.getUserMetrics();
        if (!cancelled) {
          setUserMetrics(response.data.points);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load user metrics', err);
        if (!cancelled) setError((prev) => prev ?? 'Unable to load user metrics');
      } finally {
        if (!cancelled) setUserMetricsLoading(false);
      }
    };

    const loadContractMetrics = async () => {
      setContractMetricsLoading(true);
      try {
        const response = await adminAPI.getContractMetrics();
        if (!cancelled) {
          setContractMetrics(response.data.points);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load contract metrics', err);
        if (!cancelled) setError((prev) => prev ?? 'Unable to load opportunity metrics');
      } finally {
        if (!cancelled) setContractMetricsLoading(false);
      }
    };

    loadStats();
    loadUserMetrics();
    loadContractMetrics();

    return () => {
      cancelled = true;
    };
  }, [user?.role, needsGate, refreshKey, handleAdminError]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;

    let cancelled = false;

    const fetchUsers = async () => {
      setUsersLoading(true);
      try {
        const response = await adminAPI.getUsers({ page, pageSize, search });
        if (!cancelled) {
          setUserRows(response.data.users);
          setUsersTotal(response.data.total);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load users', err);
        if (!cancelled) setError((prev) => prev ?? 'Unable to load users');
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    };

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [user?.role, needsGate, page, pageSize, search, refreshKey, handleAdminError]);

  const totalPages = useMemo(() => {
    if (usersTotal === 0) return 1;
    return Math.max(1, Math.ceil(usersTotal / pageSize));
  }, [usersTotal, pageSize]);

  const overrideOrganizationOptions = useMemo(() => {
    return Array.from(
      new Set([
        ...overrideOrgOptions,
        ...overrideOrgFilter,
        ...overrideRows.map((row) => row.procuringOrganization || row.city || ''),
      ])
    )
      .filter(Boolean)
      .sort();
  }, [overrideOrgOptions, overrideOrgFilter, overrideRows]);

  const overrideStatusOptionsResolved = useMemo(() => {
    return Array.from(
      new Set([
        ...overrideStatusOptions,
        ...overrideStatusFilter,
        ...overrideRows.map((row) => row.status || ''),
      ])
    )
      .filter(Boolean)
      .sort();
  }, [overrideStatusOptions, overrideStatusFilter, overrideRows]);

  const overrideFiltersKey = useMemo(() => {
    return [
      overrideOrgFilter.join(','),
      overrideIndustryFilter.join(','),
      overrideStatusFilter.join(','),
      overrideHasOverride,
      overrideIncludeClosed,
      overrideSearch,
      overrideMinDue,
      overrideMaxDue,
      overrideSortField,
      overrideSortDirection,
      overridePage,
      overrideShowNewOnly ? 'new' : 'all',
    ].join('|');
  }, [
    overrideOrgFilter,
    overrideIndustryFilter,
    overrideStatusFilter,
    overrideHasOverride,
    overrideIncludeClosed,
    overrideSearch,
    overrideMinDue,
    overrideMaxDue,
    overrideSortField,
    overrideSortDirection,
    overridePage,
    overrideShowNewOnly,
  ]);

  const openBillingModal = useCallback(async (userId: number) => {
    setBillingActionMessage(null);
    setBillingActionError(null);
    setBillingModalUserId(userId);
    setBillingModalOpen(true);
    setBillingLoading(true);
    try {
      const response = await adminBillingAPI.getOptions(userId);
      setBillingOptions(response.data);
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to load billing options', err);
      setError((prev) => prev ?? 'Unable to load billing options.');
      setBillingOptions(null);
    } finally {
      setBillingLoading(false);
    }
  }, [handleAdminError]);

  const closeBillingModal = useCallback(() => {
    setBillingModalOpen(false);
    setBillingModalUserId(null);
    setBillingOptions(null);
    setBillingActionLoading(null);
    setBillingActionMessage(null);
    setBillingActionError(null);
  }, []);

  const refreshBillingOptions = useCallback(async () => {
    if (!billingModalUserId) return;
    setBillingLoading(true);
    try {
      const response = await adminBillingAPI.getOptions(billingModalUserId);
      setBillingOptions(response.data);
    } catch (err) {
      console.error('Failed to refresh billing options', err);
    } finally {
      setBillingLoading(false);
    }
  }, [billingModalUserId]);

  const handleBillingAction = useCallback(async (actionId: string) => {
    if (!billingModalUserId) return;
    setBillingActionLoading(actionId);
    setBillingActionMessage(null);
    setBillingActionError(null);
    try {
      const response = await adminBillingAPI.grantAccess({ user_id: billingModalUserId, action_id: actionId });
      await refreshBillingOptions();
      setRefreshKey((prev) => prev + 1);
      setBillingActionMessage(response.data.message);
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to apply billing action', err);
      setBillingActionError(err?.response?.data?.error ?? 'Unable to apply billing action.');
    } finally {
      setBillingActionLoading(null);
    }
  }, [billingModalUserId, handleAdminError, refreshBillingOptions]);

  const handleOpenBillingPortalAdmin = useCallback(async () => {
    if (!billingModalUserId) return;
    try {
      const response = await adminBillingAPI.createPortalSession(billingModalUserId);
      const url = response.data.url;
      if (url) {
        const opened = window.open(url, '_blank', 'noopener');
        if (!opened) {
          const proceed = window.confirm(
            'Your browser blocked the billing portal popup. Click OK to open it in this tab, or Cancel to stay on the admin dashboard.'
          );
          if (proceed) {
            window.location.assign(url);
          } else {
            setError('Popup blocked. Please allow pop-ups for OppFoundry or copy the Stripe Dashboard link instead.');
          }
        }
      } else {
        setError((prev) => prev ?? 'Stripe did not return a portal link for this user.');
      }
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to open billing portal', err);
      setError((prev) => prev ?? (err?.response?.data?.error ?? 'Unable to open billing portal.'));
    }
  }, [billingModalUserId, handleAdminError]);

  const handleOpenStripeDashboard = useCallback(() => {
    if (!billingOptions?.stripe_dashboard_url) return;
    window.open(billingOptions.stripe_dashboard_url, '_blank', 'noopener');
  }, [billingOptions]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;

    let cancelled = false;
    const loadTaxonomy = async () => {
      try {
        const response = await adminAPI.getIndustryTaxonomy();
        if (!cancelled) {
          setTaxonomy(response.data.taxonomy);
        }
      } catch (err: any) {
        handleAdminError(err);
      }
    };

    loadTaxonomy();
    return () => {
      cancelled = true;
    };
  }, [user?.role, needsGate, handleAdminError]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;

    let cancelled = false;
    const loadOverrideOptions = async () => {
      try {
        const response = await adminAPI.getIndustryOverrideOptions({ includeClosed: true });
        if (!cancelled) {
          setOverrideOrgOptions(response.data.procuring_organizations ?? []);
          setOverrideStatusOptions(response.data.statuses ?? []);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load industry override filter options', err);
      }
    };

    loadOverrideOptions();
    return () => {
      cancelled = true;
    };
  }, [user?.role, needsGate, refreshKey, handleAdminError]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;
    let cancelled = false;
    const loadReviewStatus = async () => {
      setReviewStatusLoading(true);
      try {
        const response = await adminAPI.getIndustryReviewStatus();
        if (!cancelled) {
          setReviewStatus(response.data);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load review status', err);
      } finally {
        if (!cancelled) {
          setReviewStatusLoading(false);
        }
      }
    };

    loadReviewStatus();
    return () => {
      cancelled = true;
    };
  }, [user?.role, needsGate, refreshKey, handleAdminError]);

  useEffect(() => {
    if (user?.role !== 'admin' || needsGate) return;

    let cancelled = false;
    const loadOverrides = async () => {
      setOverrideLoading(true);
      try {
        const response = await adminAPI.getIndustryOverrides({
          city: overrideOrgFilter,
          industry: overrideIndustryFilter,
          status: overrideStatusFilter,
          hasOverride: overrideHasOverride === 'all' ? undefined : overrideHasOverride === 'with',
          includeClosed: overrideIncludeClosed,
          query: overrideSearch || undefined,
          minDue: overrideMinDue || undefined,
          maxDue: overrideMaxDue || undefined,
          sort: overrideSortField,
          direction: overrideSortDirection,
          page: overridePage,
          pageSize: overridePageSize,
          sinceLastReview: overrideShowNewOnly,
        });
        if (!cancelled) {
          setOverrideRows(response.data.contracts);
          setOverrideTotal(response.data.total);
        }
      } catch (err: any) {
        if (handleAdminError(err)) return;
        console.error('Failed to load override contracts', err);
        if (!cancelled) {
          setError((prev) => prev ?? 'Unable to load contracts for override management');
        }
      } finally {
        if (!cancelled) setOverrideLoading(false);
      }
    };

    loadOverrides();
    return () => {
      cancelled = true;
    };
  }, [
    user?.role,
    needsGate,
    overrideFiltersKey,
    refreshKey,
    reviewStatus?.lastReviewedAt,
    handleAdminError,
  ]);

  const handleUnlock = async (event: React.FormEvent) => {
    event.preventDefault();
    setGateSubmitting(true);
    setGateError(null);
    try {
      const response = await adminAPI.unlock(gateValue.trim());
      if (response.data.success) {
        setNeedsGate(false);
        setGateValue('');
        setRefreshKey((prev) => prev + 1);
      } else {
        setGateError('Incorrect passcode.');
      }
    } catch (err: any) {
      const code = err?.response?.data?.error;
      if (code === 'invalid_passcode') {
        setGateError('Incorrect passcode.');
      } else {
        setGateError('Unable to verify passcode.');
      }
    } finally {
      setGateSubmitting(false);
    }
  };

  const handleMarkReviewed = async () => {
    setMarkingReview(true);
    try {
      const response = await adminAPI.markIndustryReviewCheckpoint();
      setReviewStatus(response.data);
      setOverrideShowNewOnly(false);
      setOverridePage(1);
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to update review checkpoint', err);
      setError((prev) => prev ?? 'Unable to update review checkpoint right now.');
    } finally {
      setMarkingReview(false);
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="space-y-4 rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-800">
        <h1 className="text-2xl font-semibold">Admin access required</h1>
        <p className="text-sm">This section is limited to administrators. Please contact OppFoundry support if you need access.</p>
      </div>
    );
  }

  if (needsGate) {
    return (
      <AdminGatePrompt
        value={gateValue}
        onChange={setGateValue}
        onSubmit={handleUnlock}
        submitting={gateSubmitting}
        error={gateError}
      />
    );
  }

  const isInitialLoading = statsLoading && userMetricsLoading && contractMetricsLoading && usersLoading;
  const subscriptionInfo = billingOptions?.subscription ?? null;
  const trialEndRaw = subscriptionInfo?.trial_end ?? null;
  const trialEndDisplay = trialEndRaw ?? null;
  const periodEndDate = !trialEndDisplay ? subscriptionInfo?.end_date : null;
  const canOpenBillingPortal = Boolean(
    subscriptionInfo?.is_active && subscriptionInfo?.stripe_subscription_id?.startsWith('sub_')
  );

  const handleOverrideSort = (field: string, defaultDirection: 'asc' | 'desc' = 'asc') => {
    if (overrideSortField === field) {
      setOverrideSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOverrideSortField(field);
      setOverrideSortDirection(defaultDirection);
    }
    setOverridePage(1);
  };

  const handleOverrideUpdate = async (contractId: string, newValue: string | null) => {
    const previous = overrideRows.find((row) => row.contractId === contractId);
    if (!previous) return;

    setOverrideRows((prev) =>
      prev.map((row) =>
        row.contractId === contractId
          ? {
              ...row,
              industryOverride: newValue ?? null,
              industryDisplay: newValue ?? row.industryBase ?? null,
              hasOverride: newValue !== null,
            }
          : row
      )
    );

    try {
      const response = await adminAPI.updateIndustryOverride({
        contractId,
        industry: newValue,
      });
      setOverrideRows((prev) =>
        prev.map((row) => (row.contractId === contractId ? response.data.contract : row))
      );
      loadOpportunities('browse').catch((err) => console.error('Failed to refresh opportunities after override', err));
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to update override', err);
      setError((prev) => prev ?? 'Unable to update industry override');
      setOverrideRows((prev) =>
        prev.map((row) => (row.contractId === contractId ? previous : row))
      );
    }
  };

  const handleNoteUpdate = async (contractId: string, newValue: string | null) => {
    const previous = overrideRows.find((row) => row.contractId === contractId);
    if (!previous) return;

    setOverrideRows((prev) =>
      prev.map((row) => (row.contractId === contractId ? { ...row, overrideNote: newValue ?? null } : row))
    );

    try {
      const response = await adminAPI.updateIndustryOverride({
        contractId,
        note: newValue,
      });
      setOverrideRows((prev) =>
        prev.map((row) => (row.contractId === contractId ? response.data.contract : row))
      );
    } catch (err: any) {
      if (handleAdminError(err)) return;
      console.error('Failed to update override note', err);
      setError((prev) => prev ?? 'Unable to update override note');
      setOverrideRows((prev) =>
        prev.map((row) => (row.contractId === contractId ? previous : row))
      );
    }
  };

  return (
    <div className="space-y-10">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="space-y-2">
          <h1 className="text-3xl font-semibold text-gray-900">Admin Dashboard</h1>
          <p className="text-base text-gray-600">Monitor user activity, premium adoption, and new opportunities.</p>
        </div>
        <Link
          to="/grandslam/blog"
          className="inline-flex items-center rounded-xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
        >
          Open Blog CMS
        </Link>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {isInitialLoading ? (
        <LoadingSpinner message="Loading admin data..." />
      ) : (
        <>
          {stats && <StatsOverview stats={stats} />}

          <section className="grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">User growth (30 days)</h2>
                <div className="text-sm text-gray-500">
                  Total: {stats?.totalUsers.toLocaleString() ?? '—'} · Premium: {stats?.premiumUsers.toLocaleString() ?? '—'}
                </div>
              </div>
              {userMetricsLoading ? <LoadingSpinner message="Loading trend..." /> : <UserTrendChart points={userMetrics} />}
            </div>

            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Opportunities added (30 days)</h2>
                <div className="text-sm text-gray-500">
                  Total tracked: {stats?.contractCount.toLocaleString() ?? '—'}
                </div>
              </div>
              {contractMetricsLoading ? (
                <LoadingSpinner message="Loading opportunities..." />
              ) : (
                <OpportunityTrendChart points={contractMetrics} />
              )}
            </div>
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Users</h2>
                <p className="text-sm text-gray-500">Search and review every account, then open Manage Billing to issue comps, trials, or portal links.</p>
              </div>
              <input
                type="search"
                placeholder="Search by name or email"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                className="w-full max-w-xs rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>

            <div className="mt-4 overflow-hidden rounded-xl border border-gray-100">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Role</th>
                    <th className="px-4 py-3">Plan</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Billing</th>
                    <th className="px-4 py-3">Created</th>
                    <th className="px-4 py-3">Last Login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {usersLoading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center">
                        <LoadingSpinner message="Loading users..." />
                      </td>
                    </tr>
                  ) : userRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-sm text-gray-500">
                        No users found.
                      </td>
                    </tr>
                  ) : (
                    userRows.map((row) => (
                      <tr key={row.id} className="hover:bg-gray-50/60">
                        <td className="px-4 py-3">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">{row.full_name || '—'}</span>
                            <span className="text-xs text-gray-500">ID: {row.id}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{row.email}</td>
                        <td className="px-4 py-3"><RoleBadge role={row.role} /></td>
                        <td className="px-4 py-3"><PlanBadge premium={row.is_premium} canceled={row.is_premium_canceled} status={row.subscription_status} /></td>
                        <td className="px-4 py-3"><StatusBadge active={row.is_active} /></td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => openBillingModal(row.id)}
                            className="inline-flex items-center rounded-full border border-blue-200 px-3 py-1 text-xs font-semibold text-blue-600 transition hover:bg-blue-50"
                          >
                            Manage Billing
                          </button>
                        </td>
                        <td className="px-4 py-3 text-gray-700">{formatDate(row.created_at)}</td>
                        <td className="px-4 py-3 text-gray-700">{formatDate(row.last_login)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-col gap-3 border-t border-gray-100 pt-4 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
              <span>
                Showing {(usersTotal === 0 ? 0 : (page - 1) * pageSize + 1).toLocaleString()}–
                {Math.min(page * pageSize, usersTotal).toLocaleString()} of {usersTotal.toLocaleString()} users
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                  className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                >
                  Previous
                </button>
                <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  className="rounded-lg border border-gray-200 px-3 py-1 text-sm font-medium text-gray-600 transition disabled:cursor-not-allowed disabled:opacity-50 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
          </div>
          </section>

          {billingModalOpen && (
            <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 px-4 py-8">
              <div className="relative w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
                <div className="flex items-start justify-between border-b border-gray-200 px-6 py-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Manage Billing</h3>
                    <p className="text-sm text-gray-500">
                      {billingOptions?.user.email ?? 'Loading…'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={closeBillingModal}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-600 transition hover:bg-gray-200"
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className="max-h-[70vh] overflow-y-auto px-6 py-5">
                  {billingLoading ? (
                    <div className="py-16 text-center">
                      <LoadingSpinner message="Loading billing details…" />
                    </div>
                  ) : billingOptions ? (
                    <div className="space-y-6">
                      <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                        <p className="text-sm font-semibold text-gray-700">Current plan</p>
                        {subscriptionInfo ? (
                          <div className="mt-2 space-y-1 text-sm text-gray-600">
                            <p>Status: <span className="font-medium text-gray-900">{subscriptionInfo.status}</span></p>
                            <p>Active access: {subscriptionInfo.is_active ? 'Yes' : 'No'}</p>
                            {subscriptionInfo.next_invoice_discount_percent != null && (
                              <p>Next renewal discount: {subscriptionInfo.next_invoice_discount_percent}%</p>
                            )}
                            {trialEndDisplay && (
                              <p>Trial ends: {formatDate(trialEndDisplay)}</p>
                            )}
                            {periodEndDate && (
                              <p>Period ends: {formatDate(periodEndDate)}</p>
                            )}
                            {subscriptionInfo.stripe_subscription_id && (
                              <p className="break-all text-xs text-gray-500">Stripe ID: {subscriptionInfo.stripe_subscription_id}</p>
                            )}
                          </div>
                        ) : (
                          <p className="mt-2 text-sm text-gray-600">No subscription on record.</p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <h4 className="text-sm font-semibold text-gray-800">Quick links</h4>
                        <div className="flex flex-wrap gap-3">
                          <button
                            type="button"
                            onClick={handleOpenBillingPortalAdmin}
                            disabled={!canOpenBillingPortal}
                            title={canOpenBillingPortal ? undefined : 'Requires an active Stripe subscription'}
                            className="inline-flex items-center gap-2 rounded-full border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400"
                          >
                            Open Billing Portal
                          </button>
                          <button
                            type="button"
                            onClick={handleOpenStripeDashboard}
                            disabled={!billingOptions.stripe_dashboard_url}
                            className="inline-flex items-center gap-2 rounded-full border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Open Stripe Dashboard
                          </button>
                        </div>
                      </div>

                      {billingOptions.actions.length > 0 && (
                        <div className="space-y-3">
                          {billingActionMessage && <p role="status" className="text-sm text-green-700">{billingActionMessage}</p>}
                          {billingActionError && <p role="alert" className="text-sm text-red-700">{billingActionError}</p>}
                          {([
                            { type: 'trial', title: 'Free Pro access' },
                            { type: 'coupon', title: 'Next renewal discount' },
                          ] as const).map((group) => (
                            <div key={group.type} className="space-y-3">
                              <h4 className="text-sm font-semibold text-gray-800">{group.title}</h4>
                              <div className="grid gap-3 sm:grid-cols-2">
                                {billingOptions.actions.filter((action) => action.type === group.type).map((action) => (
                                  <button
                                    key={action.id}
                                    type="button"
                                    onClick={() => handleBillingAction(action.id)}
                                    disabled={action.disabled || billingActionLoading !== null}
                                    className={`flex flex-col rounded-lg border px-4 py-3 text-left transition ${
                                      action.disabled
                                        ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400'
                                        : 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100'
                                    }`}
                                  >
                                    <span className="text-sm font-semibold">
                                      {billingActionLoading === action.id ? 'Applying…' : action.label}
                                    </span>
                                    <span className="mt-1 text-xs text-gray-600">{action.description}</span>
                                    {action.disabled_reason && (
                                      <span className="mt-2 text-xs text-amber-600">{action.disabled_reason}</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="space-y-2">
                        <h4 className="text-sm font-semibold text-gray-800">Recent actions</h4>
                        {billingOptions.history.length === 0 ? (
                          <p className="text-sm text-gray-500">No prior billing adjustments recorded.</p>
                        ) : (
                          <ul className="space-y-2 text-sm text-gray-600">
                            {billingOptions.history.map((entry, idx) => (
                              <li key={`${entry.created_at}-${idx}`} className="rounded-lg border border-gray-100 bg-white px-3 py-2">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-gray-800">{entry.action}</span>
                                  <span className="text-xs text-gray-500">{formatDate(entry.created_at)}</span>
                                </div>
                                {entry.admin_email && (
                                  <p className="text-xs text-gray-500">By {entry.admin_email}</p>
                                )}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-red-600">Unable to load billing options.</p>
                  )}
                </div>

                <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-6 py-4">
                  <span className="text-xs text-gray-500">
                    Actions are logged for auditing. New adjustments sync with Stripe automatically.
                  </span>
                  <button
                    type="button"
                    onClick={closeBillingModal}
                    className="inline-flex items-center rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          )}

        <section className="space-y-6">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Industry Overrides</h2>
            <p className="mt-1 text-sm text-gray-600">
              Review contracts, adjust industries, and download labeled data for retraining the classifier.
            </p>
          </div>

          <IndustryOverrideFilters
          procuringOrganizations={overrideOrganizationOptions}
          industries={taxonomy}
          statuses={overrideStatusOptionsResolved}
            selectedOrganizations={overrideOrgFilter}
            selectedIndustries={overrideIndustryFilter}
            selectedStatuses={overrideStatusFilter}
            hasOverride={overrideHasOverride}
            includeClosed={overrideIncludeClosed}
            search={overrideSearch}
            minDue={overrideMinDue}
            maxDue={overrideMaxDue}
            onOrganizationChange={(values) => {
              setOverrideOrgFilter(values);
              setOverridePage(1);
            }}
            onIndustryChange={(values) => {
              setOverrideIndustryFilter(values);
              setOverridePage(1);
            }}
            onStatusChange={(values) => {
              setOverrideStatusFilter(values);
              setOverridePage(1);
            }}
            onHasOverrideChange={(value) => {
              setOverrideHasOverride(value);
              setOverridePage(1);
            }}
            onIncludeClosedChange={(checked) => {
              setOverrideIncludeClosed(checked);
              setOverridePage(1);
            }}
            onSearchChange={(value) => {
              setOverrideSearch(value);
              setOverridePage(1);
            }}
            onMinDueChange={(value) => {
              setOverrideMinDue(value);
              setOverridePage(1);
            }}
            onMaxDueChange={(value) => {
              setOverrideMaxDue(value);
              setOverridePage(1);
            }}
            onReset={() => {
              setOverrideOrgFilter([]);
              setOverrideIndustryFilter([]);
              setOverrideStatusFilter([]);
              setOverrideHasOverride('all');
              setOverrideIncludeClosed(false);
              setOverrideSearch('');
              setOverrideMinDue('');
              setOverrideMaxDue('');
              setOverrideSortField('due_date');
              setOverrideSortDirection('asc');
              setOverrideShowNewOnly(false);
              setOverridePage(1);
            }}
            onDownloadCsv={async () => {
              try {
                const response = await adminAPI.downloadIndustryOverridesCsv({
                  city: overrideOrgFilter,
                  industry: overrideIndustryFilter,
                  status: overrideStatusFilter,
                  hasOverride: overrideHasOverride === 'all' ? undefined : overrideHasOverride === 'with',
                  includeClosed: overrideIncludeClosed,
                  query: overrideSearch || undefined,
                  minDue: overrideMinDue || undefined,
                  maxDue: overrideMaxDue || undefined,
                  sort: overrideSortField,
                  direction: overrideSortDirection,
                });
                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', 'industry_overrides.csv');
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } catch (err: any) {
                if (handleAdminError(err)) return;
                console.error('Failed to download CSV export', err);
                setError((prev) => prev ?? 'Unable to download CSV export');
              }
            }}
          />

          <div className="rounded-2xl border border-violet-200 bg-violet-50 p-4 shadow-sm">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <p className="text-sm font-semibold text-violet-900">
                  {reviewStatusLoading && !reviewStatus
                    ? 'Checking for new contracts...'
                    : `${reviewStatus?.newSinceReview ?? 0} contract(s) added since your last review`}
                </p>
                <p className="text-xs text-violet-800">
                  Last reviewed:{' '}
                  {reviewStatus?.lastReviewedAt ? new Date(reviewStatus.lastReviewedAt).toLocaleString() : 'Never'}
                </p>
              </div>
              <div className="flex flex-col gap-2 text-sm text-violet-900 md:flex-row md:items-center">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={overrideShowNewOnly}
                    onChange={(event) => {
                      setOverrideShowNewOnly(event.target.checked);
                      setOverridePage(1);
                    }}
                    className="h-4 w-4 rounded border-violet-300 text-violet-600 focus:ring-violet-500"
                  />
                  Show only new since last review
                </label>
                <button
                  type="button"
                  onClick={handleMarkReviewed}
                  disabled={markingReview}
                  className="inline-flex items-center justify-center rounded-full bg-violet-600 px-4 py-1.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:bg-violet-400"
                >
                  {markingReview ? 'Saving…' : 'Mark current queue as reviewed'}
                </button>
              </div>
            </div>
          </div>

          <IndustryOverrideTable
            rows={overrideRows}
          taxonomy={taxonomy}
          sortField={overrideSortField}
          sortDirection={overrideSortDirection}
          loading={overrideLoading}
          page={overridePage}
          pageSize={overridePageSize}
          total={overrideTotal}
          onSort={handleOverrideSort}
          onPageChange={setOverridePage}
          onOverrideChange={handleOverrideUpdate}
          onNoteChange={handleNoteUpdate}
        />
      </section>
      </>
    )}
  </div>
  );
};

export default Admin;

const AdminGatePrompt: React.FC<{
  value: string;
  onChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
  submitting: boolean;
  error: string | null;
}> = ({ value, onChange, onSubmit, submitting, error }) => (
  <div className="mx-auto max-w-md space-y-6 rounded-2xl border border-blue-200 bg-white p-8 shadow-sm">
    <div className="space-y-2 text-center">
      <h1 className="text-2xl font-semibold text-gray-900">Enter admin passcode</h1>
      <p className="text-sm text-gray-600">This area contains sensitive user data. Please provide the additional passcode.</p>
    </div>
    <form className="space-y-4" onSubmit={onSubmit}>
      <div className="space-y-2">
        <label htmlFor="admin-passcode" className="block text-sm font-medium text-gray-700">
          Admin UI Passcode
        </label>
        <input
          id="admin-passcode"
          type="password"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          placeholder="Enter passcode"
          autoComplete="off"
          required
        />
        {error && <p className="text-xs text-red-600">{error}</p>}
      </div>
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex w-full items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? 'Verifying…' : 'Unlock dashboard'}
      </button>
    </form>
  </div>
);

const StatsOverview: React.FC<{ stats: AdminStats }> = ({ stats }) => {
  const statItems = [
    { label: 'Total users', value: stats.totalUsers, tone: 'text-blue-700 bg-blue-50' },
    { label: 'Premium users', value: stats.premiumUsers, tone: 'text-violet-700 bg-violet-50' },
    { label: 'Free users', value: stats.freeUsers, tone: 'text-slate-700 bg-slate-100' },
    { label: 'New signups (24h)', value: stats.newUsersDay, tone: 'text-emerald-700 bg-emerald-50' },
    { label: 'New signups (7d)', value: stats.newUsersWeek, tone: 'text-emerald-700 bg-emerald-50' },
    { label: 'New signups (30d)', value: stats.newUsersMonth, tone: 'text-amber-700 bg-amber-50' },
    { label: 'Opportunities tracked', value: stats.contractCount, tone: 'text-gray-700 bg-gray-100' },
  ];

  return (
    <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {statItems.map((item) => (
        <div key={item.label} className="flex flex-col justify-between rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</span>
          <div className={`mt-3 inline-flex rounded-xl px-3 py-2 text-xl font-semibold ${item.tone}`}>
            {item.value.toLocaleString()}
          </div>
        </div>
      ))}
    </section>
  );
};

const UserTrendChart: React.FC<{ points: AdminUserMetricPoint[] }> = ({ points }) => {
  if (!points.length) {
    return <p className="text-sm text-gray-500">No historical data yet.</p>;
  }

  const width = 640;
  const height = 200;
  const maxTotal = Math.max(...points.map((p) => p.totalUsers)) || 1;
  const xStep = points.length > 1 ? width / (points.length - 1) : width;

  const toCoords = (values: number[]) =>
    values
      .map((val, idx) => {
        const x = idx * xStep;
        const y = height - (val / maxTotal) * (height - 20) - 10;
        return `${x},${y}`;
      })
      .join(' ');

  const totalPath = toCoords(points.map((p) => p.totalUsers));
  const premiumPath = toCoords(points.map((p) => p.premiumUsers));

  const tickIndexes = [0, Math.floor(points.length / 2), points.length - 1];
  const uniqueTicks = Array.from(new Set(tickIndexes));

  return (
    <div className="space-y-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-56 w-full max-w-full">
        <defs>
          <linearGradient id="totalGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#2563eb" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#2563eb" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="premiumGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#a855f7" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#a855f7" stopOpacity="0" />
          </linearGradient>
        </defs>

        <polyline points={totalPath} fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" />
        <polyline points={premiumPath} fill="none" stroke="#a855f7" strokeWidth="2.5" strokeLinecap="round" />

        <polygon points={`${totalPath} ${width},${height} 0,${height}`} fill="url(#totalGradient)" opacity={0.4} />
        <polygon points={`${premiumPath} ${width},${height} 0,${height}`} fill="url(#premiumGradient)" opacity={0.4} />

        <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#e2e8f0" />
      </svg>
      <div className="flex items-center justify-between text-xs text-gray-500">
        {uniqueTicks.map((idx) => {
          const point = points[idx];
          return (
            <span key={point.date}>
              {new Date(point.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
        <LegendDot color="bg-blue-500" label="Total users" />
        <LegendDot color="bg-violet-500" label="Premium users" />
        <span className="text-xs text-gray-500">
          Yesterday new signups: {points[points.length - 1]?.newUsers.toLocaleString() ?? 0}
        </span>
      </div>
    </div>
  );
};

const OpportunityTrendChart: React.FC<{ points: AdminContractMetricPoint[] }> = ({ points }) => {
  if (!points.length) {
    return <p className="text-sm text-gray-500">No opportunity history yet.</p>;
  }

  const width = 640;
  const height = 200;
  const maxTotal = Math.max(...points.map((p) => p.cumulativeContracts)) || 1;
  const xStep = points.length > 1 ? width / (points.length - 1) : width;

  const cumulativePath = points
    .map((point, idx) => {
      const x = idx * xStep;
      const y = height - (point.cumulativeContracts / maxTotal) * (height - 20) - 10;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div className="space-y-4">
      <svg viewBox={`0 0 ${width} ${height}`} className="h-56 w-full max-w-full">
        <defs>
          <linearGradient id="contractGradient" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0ea5e9" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0ea5e9" stopOpacity="0" />
          </linearGradient>
        </defs>
        <polyline points={cumulativePath} fill="none" stroke="#0ea5e9" strokeWidth="2.5" strokeLinecap="round" />
        <polygon points={`${cumulativePath} ${width},${height} 0,${height}`} fill="url(#contractGradient)" opacity={0.4} />
        <line x1={0} y1={height - 1} x2={width} y2={height - 1} stroke="#e2e8f0" />
      </svg>
      <div className="flex items-center justify-between text-xs text-gray-500">
        {[0, Math.floor(points.length / 2), points.length - 1].map((idx) => {
          const point = points[idx];
          return (
            <span key={point.date}>
              {new Date(point.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
            </span>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
        <LegendDot color="bg-sky-500" label="Cumulative opportunities" />
        <span className="text-xs text-gray-500">
          Yesterday new: {points[points.length - 1]?.newContracts.toLocaleString() ?? 0}
        </span>
      </div>
    </div>
  );
};

const LegendDot: React.FC<{ color: string; label: string }> = ({ color, label }) => (
  <span className="inline-flex items-center gap-2 text-xs font-medium text-gray-600">
    <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
    {label}
  </span>
);

const RoleBadge: React.FC<{ role: string }> = ({ role }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
    {role === 'admin' ? 'Admin' : 'User'}
  </span>
);

const PlanBadge: React.FC<{ premium: boolean; canceled?: boolean; status?: string | null }> = ({
  premium,
  canceled = false,
  status,
}) => {
  if (!premium) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
        Free
      </span>
    );
  }

  if (status === 'trialing') {
    return (
      <span className="inline-flex items-center rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
        Pro-T
      </span>
    );
  }

  if (canceled) {
    return (
      <span className="inline-flex items-center rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">
        Pro-C
      </span>
    );
  }

  if (status === 'past_due') {
    return (
      <span className="inline-flex items-center rounded-full bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-700">
        Pro (past due)
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-violet-100 px-3 py-1 text-xs font-semibold text-violet-700">
      Pro
    </span>
  );
};

const StatusBadge: React.FC<{ active: boolean }> = ({ active }) => (
  <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${
    active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'
  }`}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
